import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Eye, AlertOctagon, PackageX, RotateCcw, Loader2, User, HelpCircle, MessageCircleQuestion } from "lucide-react";

// IS IT THERE, AND IS IT ANY GOOD — one control, because they are one decision at the truck.
// Five taps, and the ABSENCE of a tap is its own honest state ("nobody has looked"). Nothing here
// demands all ten components before it accepts one: each tap saves on its own, so a tech who only
// knows about the engine sets the engine and walks away.
//
// GOOD IS NOT GATED ON WHICH SCREEN YOU ARE ON (owner ruling 2026-09-25). He knows good
// components — his foreman ran it, it drove in, he tested it last month — and a rule about where
// he is sitting stops a TRUE fact getting recorded. The gate is WHO HAD HANDS ON IT AND HOW, and
// that provenance travels with the state so the answer at the counter is never a bare "GOOD".
const TONE = {
  good:    { box: "border-emerald-500 bg-emerald-50 text-emerald-800", dot: "bg-emerald-500", icon: CheckCircle2 },
  plain:   { box: "border-sky-500 bg-sky-50 text-sky-800",             dot: "bg-sky-500",     icon: Eye },
  bad:     { box: "border-[#B01E28] bg-red-50 text-[#B01E28]",         dot: "bg-[#B01E28]",   icon: AlertOctagon },
  looked:  { box: "border-[#E0781A] bg-orange-50 text-[#C76714]",      dot: "bg-[#E0781A]",   icon: HelpCircle },
  gone:    { box: "border-slate-700 bg-slate-800 text-white",          dot: "bg-slate-700",   icon: PackageX },
  unknown: { box: "border-dashed border-slate-300 bg-white text-slate-500", dot: "bg-slate-300", icon: Eye },
};
const ORDER = ["present_good", "present_unverified", "cant_tell", "present_bad", "gone"];

export function ConditionBadge({ row, className = "" }) {
  const t = TONE[row?.tone || "unknown"] || TONE.unknown;
  const Icon = t.icon;
  return (
    <span data-testid={`cond-state-${row?.component}`}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${t.box} ${className}`}>
      <Icon className="w-3 h-3" />{row?.state_short || "NOT ASSESSED"}
      {row?.reason_label ? <span className="font-normal normal-case">· {row.reason_label}</span> : null}
      {/* WHO SAYS SO travels with the word, on both the good news and the bad. A bare GOOD leaves
          the next question at the counter unanswered — and so does a bare KNOWN BAD, because the
          first thing a foreman asks about one is who called it. */}
      {["present_good", "present_bad"].includes(row?.state) && row?.provenance?.line
        ? <span data-testid={`cond-prov-badge-${row?.component}`} className="font-normal normal-case">· {row.provenance.line}</span>
        : null}
    </span>
  );
}

/** SOMEBODY IS ASKING ABOUT THIS ONE — on the component's own row, so the desk panel and the
 *  FLOOR card both carry it without either knowing this feature exists. A customer is waiting at
 *  the other end of it, which is why it sits above the buttons and not in a log. */
function AskBanner({ donorId, row, canDismiss, dismissReasons = [], big, onSaved }) {
  const ask = row?.ask;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!ask) return null;

  const dismiss = async (code) => {
    setBusy(true);
    try {
      await api.post(`/requests/assess/${ask.id}/dismiss`, { reason: code, note });
      toast.success("Closed — everybody who asked has been told why");
      setOpen(false); setReason(null); setNote("");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`cond-ask-${row.component}`}
      className={`rounded-lg border-2 border-[#E0781A]/60 bg-[#FFF7ED] ${big ? "p-3" : "p-2"} space-y-1.5`}>
      <p className={`flex items-start gap-1.5 font-bold text-[#8a4b0b] ${big ? "text-base" : "text-[12px]"}`}>
        <MessageCircleQuestion className={big ? "w-5 h-5 shrink-0 mt-0.5" : "w-4 h-4 shrink-0 mt-px"} />
        <span data-testid={`cond-ask-line-${row.component}`}>{ask.line}</span>
      </p>
      {/* EVERY ASKER'S OWN NOTE. "Needs it by Friday" and "just checking" are different
          urgencies, and folding them into one line would lose that. */}
      {(ask.askers || []).filter((a) => a.note).map((a, i) => (
        <p key={i} data-testid={`cond-ask-note-${row.component}-${i}`}
          className={`text-[#8a5a12] ${big ? "text-sm" : "text-[11px]"}`}>
          “{a.note}” — {a.name}{a.role ? ` (${a.role})` : ""}
        </p>
      ))}
      {(ask.looked_cant_tell || []).length > 0 && (
        <p data-testid={`cond-ask-stood-${row.component}`}
          className={`text-slate-600 ${big ? "text-sm" : "text-[11px]"}`}>
          <b>{ask.looked_cant_tell[ask.looked_cant_tell.length - 1].name}</b> went out and still
          couldn’t tell — this one needs more than a look.
        </p>
      )}
      <p className={`text-[#8a5a12]/80 ${big ? "text-sm" : "text-[11px]"}`}>
        Rating it below answers them automatically.
      </p>
      {canDismiss && (open ? (
        <div data-testid={`cond-ask-dismiss-panel-${row.component}`} className="space-y-1.5 pt-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Close it without a rating — why?
          </p>
          <div className="flex flex-wrap gap-1.5">
            {dismissReasons.map((r) => (
              <button key={r.code} type="button" data-testid={`cond-ask-reason-${row.component}-${r.code}`}
                disabled={busy}
                onClick={() => (r.code === "other" ? setReason("other") : dismiss(r.code))}
                className={`rounded-lg border-2 font-bold disabled:opacity-40
                  ${reason === r.code ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 bg-white text-slate-600"}
                  ${big ? "h-12 px-3 text-[13px]" : "h-7 px-2 text-[11px]"}`}>
                {r.label}
              </button>
            ))}
          </div>
          {reason === "other" && (
            <div className="flex items-center gap-1.5">
              <Input data-testid={`cond-ask-other-${row.component}`} value={note}
                onChange={(e) => setNote(e.target.value)} placeholder="Say why in a word or two"
                className={`rounded-lg border-slate-300 ${big ? "h-12 text-base" : "h-7 text-xs"}`} />
              <button type="button" data-testid={`cond-ask-other-save-${row.component}`}
                disabled={busy || !note.trim()} onClick={() => dismiss("other")}
                className={`rounded-lg bg-slate-800 text-white font-bold disabled:opacity-40 ${big ? "h-12 px-4" : "h-7 px-3 text-xs"}`}>
                Close it
              </button>
            </div>
          )}
          <button type="button" data-testid={`cond-ask-dismiss-cancel-${row.component}`}
            onClick={() => { setOpen(false); setReason(null); }}
            className="text-[11px] text-slate-500 underline">cancel</button>
        </div>
      ) : (
        <button type="button" data-testid={`cond-ask-dismiss-${row.component}`} onClick={() => setOpen(true)}
          className="text-[11px] font-semibold text-slate-500 underline hover:text-slate-800">
          close it without a rating
        </button>
      ))}
    </div>
  );
}

export function ConditionControl({ donorId, row, states = [], reasons = [], otherCode = "other",
                                   canAssess, canDismiss = false, dismissReasons = [],
                                   onSaved, big = false, surface = "desk" }) {
  const { user } = useAuth();
  const myName = (user?.name || "").trim() || user?.email || "";
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null);          // "good" | "bad" | null
  const [other, setOther] = useState("");
  const [how, setHow] = useState("hands_on");        // hands_on | reported
  const [who, setWho] = useState("");
  const comp = row?.component;
  const meta = (code) => states.find((s) => s.code === code) || {};
  const savedReason = row?.state === "present_bad" ? row?.reason : null;

  // RE-OPENING A PICKER MUST NOT LOSE WHAT IS ALREADY ON RECORD. A blank panel over a saved
  // "seized" is how a tech clears a word he meant to keep — with no error and nobody the wiser.
  const openBad = () => {
    setOther(savedReason === otherCode ? (row?.note || "") : "");
    setHow(row?.method === "reported" ? "reported" : "hands_on");
    setWho(row?.method === "reported" ? (row?.assessed_by || "") : "");
    setPanel("bad");
  };
  const openGood = () => {
    const reported = row?.method === "reported";
    setHow(reported ? "reported" : "hands_on");
    setWho(reported ? (row?.assessed_by || "") : myName);
    setPanel("good");
  };

  const save = async (state, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/donors/${donorId}/condition/${comp}`,
        { state, reason: null, note: null, method: null, assessed_by: null,
          via: surface === "floor" ? "floor" : "desk", ...extra });
      setPanel(null); setOther("");
      onSaved?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // KNOWN BAD is information somebody gave you, same as GOOD — so it carries a name too when it
  // came from a person rather than from your own eyes.
  const badProv = () => (how === "reported"
    ? { method: "reported", assessed_by: who.trim() }
    : { method: "looked", assessed_by: myName });
  const badBlocked = how === "reported" && !who.trim();

  const btn = (code) => {
    const m = meta(code);
    const t = TONE[m.tone || "unknown"] || TONE.unknown;
    const on = row?.state === code && !row?.derived_gone;
    const open = (code === "present_bad" ? openBad : code === "present_good" ? openGood : null);
    return (
      <button key={code} type="button" data-testid={`cond-set-${comp}-${code}`}
        disabled={busy || !canAssess}
        onClick={() => (open ? open() : save(code))}
        title={m.help}
        className={`rounded-lg border-2 font-bold uppercase tracking-wide transition-colors disabled:opacity-40
          ${big ? "h-14 px-3 text-[13px]" : "h-8 px-2.5 text-[10px]"}
          ${on ? t.box : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`}>
        {m.short || code}
      </button>
    );
  };

  return (
    <div data-testid={`cond-row-${comp}`} className="space-y-2">
      <AskBanner donorId={donorId} row={row} canDismiss={canDismiss}
        dismissReasons={dismissReasons} big={big} onSaved={() => onSaved?.()} />      {/* THE DERIVED FACT WINS. A part sitting in a bin is not on the truck, whatever anyone
          tapped earlier — so it says so, and says why, instead of arguing with the record. */}
      {row?.derived_gone && (
        <p data-testid={`cond-derived-${comp}`} className="text-[11px] text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1.5">
          <b>GONE</b> — {row.derived_why}
          {row.contradicts ? <span className="text-[#B01E28]"> · somebody had marked it {row.contradicts}</span> : null}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {ORDER.map(btn)}
        {canAssess && row?.state !== "not_assessed" && !row?.derived_gone && (
          <button type="button" data-testid={`cond-clear-${comp}`} disabled={busy}
            onClick={() => save("not_assessed")}
            className={`rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 ${big ? "h-14 px-3" : "h-8 px-2"}`}
            title="Back to 'nobody has looked'">
            <RotateCcw className={big ? "w-5 h-5" : "w-3.5 h-3.5"} />
          </button>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {/* GOOD — the one state that has to name somebody. Never a quick tick. */}
      {panel === "good" && (
        <div data-testid={`cond-good-panel-${comp}`} className="rounded-lg border-2 border-emerald-300 bg-emerald-50/70 p-2 space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
            Who had hands on it?
          </p>
          <p className="text-[11px] text-emerald-900/80">
            <b>GOOD</b> is what a customer buys against, so it can't come from a look or a
            photograph — but it doesn't matter where you're sitting either. It matters that
            somebody put hands on it and you can <b>name them</b>.
          </p>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" data-testid={`cond-good-me-${comp}`}
              onClick={() => { setHow("hands_on"); setWho(myName); }}
              className={`rounded-lg border-2 font-bold ${big ? "h-14 px-4 text-[13px]" : "h-8 px-2.5 text-[11px]"} ${how === "hands_on" ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-300 bg-white text-emerald-800"}`}>
              I had hands on it
            </button>
            <button type="button" data-testid={`cond-good-reported-${comp}`}
              onClick={() => { setHow("reported"); setWho(row?.method === "reported" ? (row?.assessed_by || "") : ""); }}
              className={`rounded-lg border-2 font-bold ${big ? "h-14 px-4 text-[13px]" : "h-8 px-2.5 text-[11px]"} ${how === "reported" ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-300 bg-white text-emerald-800"}`}>
              Somebody who did told me
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <Input data-testid={`cond-good-name-${comp}`} value={who} onChange={(e) => setWho(e.target.value)}
              placeholder={how === "reported" ? "Who told you? (a name)" : "Your name"}
              className={`rounded-lg border-emerald-300 ${big ? "h-14 text-base" : "h-8 text-xs"}`} />
            <button type="button" data-testid={`cond-good-save-${comp}`}
              disabled={busy || !who.trim()}
              onClick={() => save("present_good", { method: how, assessed_by: who.trim() })}
              className={`rounded-lg bg-emerald-600 text-white font-bold disabled:opacity-40 ${big ? "h-14 px-4" : "h-8 px-3 text-xs"}`}>
              Record GOOD
            </button>
          </div>
          <button type="button" data-testid={`cond-good-cancel-${comp}`} onClick={() => setPanel(null)}
            className="text-[11px] text-slate-500 underline">cancel</button>
        </div>
      )}

      {/* REASON WORDS — one tap, gloves on. Free text only behind Other. */}
      {panel === "bad" && (
        <div data-testid={`cond-reasons-${comp}`} className="rounded-lg border border-[#B01E28]/30 bg-red-50/60 p-2 space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#B01E28]">Why is it bad?</p>
          {savedReason && (
            <p data-testid={`cond-reason-saved-${comp}`} className="text-[11px] text-slate-600">
              Already saved: <b>{row?.reason_label || savedReason}</b>
              {savedReason === otherCode && row?.note ? <> — “{row.note}”</> : null}
              . Tapping a different word replaces it; <i>cancel</i> keeps it.
            </p>
          )}
          {/* Bad news is information too — if a person told you, their name goes on it. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Who says so?</span>
            <button type="button" data-testid={`cond-bad-seen-${comp}`} onClick={() => setHow("hands_on")}
              className={`rounded-lg border px-2 h-7 text-[11px] font-bold ${how !== "reported" ? "border-[#B01E28] bg-[#B01E28] text-white" : "border-slate-300 bg-white text-slate-600"}`}>
              I can see it
            </button>
            <button type="button" data-testid={`cond-bad-reported-${comp}`} onClick={() => setHow("reported")}
              className={`rounded-lg border px-2 h-7 text-[11px] font-bold ${how === "reported" ? "border-[#B01E28] bg-[#B01E28] text-white" : "border-slate-300 bg-white text-slate-600"}`}>
              Somebody told me
            </button>
            {how === "reported" && (
              <Input data-testid={`cond-bad-name-${comp}`} value={who} onChange={(e) => setWho(e.target.value)}
                placeholder="Who told you? (a name)" className="h-7 w-44 text-xs rounded-lg border-slate-300" />
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {reasons.map((r) => (
              <button key={r.code} type="button" data-testid={`cond-reason-${comp}-${r.code}`}
                disabled={busy || badBlocked}
                aria-pressed={savedReason === r.code}
                onClick={() => save("present_bad", { reason: r.code, ...badProv() })}
                className={`rounded-lg border-2 font-bold hover:bg-red-100 disabled:opacity-40
                  ${savedReason === r.code
                    ? "border-[#B01E28] bg-[#B01E28] text-white hover:bg-[#B01E28]"
                    : "border-[#B01E28]/40 bg-white text-[#B01E28]"}
                  ${big ? "h-14 px-4 text-[13px]" : "h-8 px-2.5 text-[11px]"}`}>{r.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Input data-testid={`cond-reason-other-${comp}`} value={other} onChange={(e) => setOther(e.target.value)}
              placeholder="Other — say it in a word or two"
              className={`rounded-lg border-slate-300 ${big ? "h-14 text-base" : "h-8 text-xs"}`} />
            <button type="button" data-testid={`cond-reason-other-save-${comp}`}
              disabled={busy || !other.trim() || badBlocked}
              onClick={() => save("present_bad", { reason: otherCode, note: other, ...badProv() })}
              className={`rounded-lg bg-[#B01E28] text-white font-bold disabled:opacity-40 ${big ? "h-14 px-4" : "h-8 px-3 text-xs"}`}>
              Save
            </button>
          </div>
          {badBlocked && (
            <p data-testid={`cond-bad-needs-name-${comp}`} className="text-[11px] text-[#B01E28]">
              Type the name of whoever told you — a report with nobody's name on it can't be checked later.
            </p>
          )}
          <button type="button" data-testid={`cond-reason-cancel-${comp}`} onClick={() => setPanel(null)}
            className="text-[11px] text-slate-500 underline">cancel</button>
        </div>
      )}

      {/* WHO SAID SO. A foreman who disagrees with a rating can see whose call it was before he
          changes it — on the component itself, not buried in a log. */}
      {(row?.provenance?.line || row?.by) && (
        <p data-testid={`cond-who-${comp}`} className="text-[10px] text-slate-400 font-mono flex flex-wrap items-center gap-1">
          <User className="w-3 h-3" />
          {row?.provenance?.line
            ? <span data-testid={`cond-prov-${comp}`} className="text-slate-500">{row.provenance.line}</span>
            : null}
          {row?.by ? <span>{row.provenance?.line ? "· recorded by " : ""}{row.by}</span> : null}
          {!row?.provenance?.line && row?.at ? <span>· {String(row.at).slice(0, 16).replace("T", " ")}</span> : null}
          {row.via_label ? <span className="text-slate-400"> · {row.via_label}</span> : null}
          {row.added_in_review ? <span className="text-[#C76714] font-sans"> · noticed during a review, not off a sheet</span> : null}
          {row.note ? <span className="text-slate-500 font-sans"> · “{row.note}”</span> : null}
        </p>
      )}
      {!canAssess && row?.state === "not_assessed" && (
        <p className="text-[10px] text-slate-400">Rating components needs the “Rate component condition” permission.</p>
      )}
    </div>
  );
}

export function ConditionSummaryLine({ summary, testid = "condition-summary" }) {
  if (!summary) return null;
  return (
    <p data-testid={testid} className="font-mono text-[11px] text-slate-500">
      {summary.sentence}
      {summary.any_bad ? <b className="text-[#B01E28]"> · known bad still sells as a core</b> : null}
    </p>
  );
}
